import { useNavigate, useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { OverlayPage } from "@/components/layout/OverlayPage";
import { OverlayContent } from "@/components/layout/OverlayContent";
import { OverlaySection, OverlaySectionGroup, OverlaySummaryRow } from "@/components/layout/OverlaySection";
import { useLanguage } from "@/contexts/LanguageContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ProductListEditor } from "@/components/orders/ProductListEditor";
import { DeliveryDatePicker } from "@/components/orders/DeliveryDatePicker";
import { ContextTagBadge } from "@/components/ContextTagBadge";
import { PhoneDisplay } from "@/components/ui/PhoneDisplay";
import { ContextTagSelect } from "@/components/ContextTagSelect";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ConfirmDialog } from "@/components/ui/dialogs";
import { Loader2, RefreshCw, Send, Trash2, Copy, Check, ExternalLink } from "lucide-react";
import { toast as sonnerToast } from "sonner";
import { StateBlock, InlineSummaryRow, FieldStack } from "@/components/sheet";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ORDER_CATEGORIES, ORDER_SOURCES, CURRENCY } from "@/lib/constants";
import { getNextStatuses, ALL_OVERRIDE_STATUSES } from "@/lib/orderStateMachine";
import { formatDetailDate, formatTime } from "@/lib/dateUtils";
import type { Order } from "@/types/order";
import { getCategoryLabel, getSourceLabel } from "@/translations";
import { orderDetailSchema } from "@/lib/validationSchemas";
import { useDebounceCallback } from "@/hooks/useDebounceCallback";
import { useUserSettings } from "@/hooks/useUserSettings";
import { createActivityEntry, createLogEntry, parseLogRole, stripRoleTokenForDisplay } from "@/lib/deliveryLogFormat";
import { appendNoteAtomic } from "@/lib/atomicNoteAppend";
import { RoleIcon } from "@/components/ui/RoleIcon";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useOverlayState } from "@/hooks/useOverlayState";
import { useAcknowledgeOrder } from "@/hooks/useAcknowledgeOrder";

export default function OrderDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { acknowledgeOrder } = useAcknowledgeOrder();
  const { data: userSettings } = useUserSettings();
  const orderContexts = userSettings?.orderContexts || [];

  // Fetch order data - uses initialData from cache for instant open when clicking from table
  const { data: order, isLoading: orderLoading } = useQuery({
    queryKey: ["order", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data as Order;
    },
    enabled: !!id,
    // Use cached data immediately if available (pre-populated by Index.tsx handleViewOrder)
    initialData: () => queryClient.getQueryData<Order>(["order", id]),
    staleTime: 30 * 1000, // Consider data fresh for 30s to avoid refetch flash
  });

  // Form state
  const [orderNumber, setOrderNumber] = useState("");
  const [status, setStatus] = useState("New");
  const [category, setCategory] = useState("");
  const [contextTag, setContextTag] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [parsedItems, setParsedItems] = useState("");
  const [address, setAddress] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [comments, setComments] = useState("");
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState<string | null>(null);
  const [internalNotes, setInternalNotes] = useState("");
  const [newLogEntry, setNewLogEntry] = useState("");
  const [loading, setLoading] = useState(false);
  const [flashLogIndex, setFlashLogIndex] = useState<number | null>(null);
  const [showRawParsed, setShowRawParsed] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
  const [showStatusOverrideConfirm, setShowStatusOverrideConfirm] = useState(false);
  const [pendingStatusValue, setPendingStatusValue] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const logScrollRef = useRef<HTMLDivElement>(null);
  const populatedOrderIdRef = useRef<string | null>(null);
  
  // Editable customer fields (point #3)
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [source, setSource] = useState("");
  
  // Confirmation dialogs for critical fields (point #3)
  const [showOrderNumberConfirm, setShowOrderNumberConfirm] = useState(false);
  const [pendingOrderNumber, setPendingOrderNumber] = useState("");

  // Track original values for dirty state detection
  const [originalValues, setOriginalValues] = useState({
    orderNumber: "",
    status: "New",
    category: "",
    contextTag: "",
    assignedTo: "",
    parsedItems: "",
    address: "",
    totalPrice: "",
    comments: "",
    requestedDeliveryDate: "" as string | null,
    customerName: "",
    customerPhone: "",
    source: "",
  });

  // Track which fields have been modified
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());

  // Sync state when order loads — only on ID change to prevent cache-patch re-renders
  useEffect(() => {
    if (!order) return;
    if (order.id === populatedOrderIdRef.current) return;

    const values = {
      orderNumber: order.order_number || "",
      status: order.order_status || "New",
      category: order.category || "",
      contextTag: order.context_tag || "",
      assignedTo: order.assigned_to || "",
      parsedItems: order.parsed_items || "",
      address: order.address || "",
      totalPrice: order.total_price?.toString() || "",
      comments: order.comments || "",
      requestedDeliveryDate: order.requested_delivery_date || null,
      customerName: order.customer_name || "",
      customerPhone: order.customer_phone || "",
      source: order.source || "",
    };

    setOrderNumber(values.orderNumber);
    setStatus(values.status);
    setCategory(values.category);
    setContextTag(values.contextTag);
    setAssignedTo(values.assignedTo);
    setParsedItems(values.parsedItems);
    setAddress(values.address);
    setTotalPrice(values.totalPrice);
    setComments(values.comments);
    setRequestedDeliveryDate(values.requestedDeliveryDate);
    setInternalNotes(order.internal_notes || "");
    setNewLogEntry("");
    setCustomerName(values.customerName);
    setCustomerPhone(values.customerPhone);
    setSource(values.source);

    setOriginalValues(values);
    setDirtyFields(new Set());
    populatedOrderIdRef.current = order.id;
  }, [order]);

  // Acknowledge order on first view (removes NEW badge, logs forensic entry)
  useEffect(() => {
    if (order?.id && !order.acknowledged_at) {
      acknowledgeOrder(order.id, "detail_open");
    }
  }, [order?.id, order?.acknowledged_at, acknowledgeOrder]);

  // Check if any fields have been modified
  const hasUnsavedChanges = dirtyFields.size > 0;
  
  // Use overlay state machine for processing state (integrates with OverlayPage)
  const overlay = useOverlayState({ externalIsDirty: hasUnsavedChanges });

  // Memoize formatted values
  const formattedCreatedDate = useMemo(
    () => (order?.created_at ? formatDetailDate(order.created_at, language) : ""),
    [order?.created_at, language]
  );
  const formattedCreatedTime = useMemo(
    () => (order?.created_at ? formatTime(order.created_at, language) : ""),
    [order?.created_at, language]
  );
  const formattedUpdatedDate = useMemo(
    () => (order?.updated_at ? formatDetailDate(order.updated_at, language) : ""),
    [order?.updated_at, language]
  );
  const formattedUpdatedTime = useMemo(
    () => (order?.updated_at ? formatTime(order.updated_at, language) : ""),
    [order?.updated_at, language]
  );
  const formattedSource = useMemo(
    () => (order?.source ? getSourceLabel(order.source, language) : ""),
    [order?.source, language]
  );

  // Helper to track field changes (core logic without debouncing)
  const trackFieldChangeCore = useCallback(
    (fieldName: string, newValue: string) => {
      if (!order) return;

      const originalValue = originalValues[fieldName as keyof typeof originalValues];
      const isModified = newValue !== originalValue;

      setDirtyFields((prev) => {
        const updated = new Set(prev);
        if (isModified) {
          updated.add(fieldName);
        } else {
          updated.delete(fieldName);
        }
        return updated;
      });
    },
    [order, originalValues]
  );

  // Debounced version for input handlers (150ms delay)
  const trackFieldChange = useDebounceCallback(trackFieldChangeCore, 150);

  // Memoized change handlers
  const handleStatusChange = useCallback(
    (value: string) => {
      // Gate: if changing FROM a terminal status, require confirmation
      const terminalStatuses = ["Delivered", "Cancelled"];
      if (order && terminalStatuses.includes(order.order_status) && !terminalStatuses.includes(value)) {
        setPendingStatusValue(value);
        setShowStatusOverrideConfirm(true);
        return;
      }
      setStatus(value);
      trackFieldChange("status", value);
    },
    [trackFieldChange, order]
  );

  const confirmStatusOverride = useCallback(() => {
    if (pendingStatusValue) {
      setStatus(pendingStatusValue);
      trackFieldChange("status", pendingStatusValue);
    }
    setShowStatusOverrideConfirm(false);
    setPendingStatusValue(null);
  }, [pendingStatusValue, trackFieldChange]);

  const cancelStatusOverride = useCallback(() => {
    setShowStatusOverrideConfirm(false);
    setPendingStatusValue(null);
  }, []);

  const handleCategoryChange = useCallback(
    (value: string) => {
      setCategory(value);
      trackFieldChange("category", value);
    },
    [trackFieldChange]
  );

  const handleContextTagChange = useCallback(
    (value: string) => {
      setContextTag(value);
      trackFieldChange("contextTag", value);
    },
    [trackFieldChange]
  );

  // Order number requires confirmation (point #3)
  const handleOrderNumberBlur = useCallback(() => {
    if (orderNumber !== originalValues.orderNumber && orderNumber.trim()) {
      setPendingOrderNumber(orderNumber);
      setShowOrderNumberConfirm(true);
    }
  }, [orderNumber, originalValues.orderNumber]);

  const confirmOrderNumberChange = useCallback(() => {
    trackFieldChangeCore("orderNumber", pendingOrderNumber);
    setShowOrderNumberConfirm(false);
  }, [pendingOrderNumber, trackFieldChangeCore]);

  const cancelOrderNumberChange = useCallback(() => {
    setOrderNumber(originalValues.orderNumber);
    setShowOrderNumberConfirm(false);
    setPendingOrderNumber("");
  }, [originalValues.orderNumber]);

  const handleOrderNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setOrderNumber(e.target.value);
    },
    []
  );
  
  const handleCustomerNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCustomerName(e.target.value);
      trackFieldChange("customerName", e.target.value);
    },
    [trackFieldChange]
  );
  
  const handleCustomerPhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCustomerPhone(e.target.value);
      trackFieldChange("customerPhone", e.target.value);
    },
    [trackFieldChange]
  );
  
  const handleSourceChange = useCallback(
    (value: string) => {
      setSource(value);
      trackFieldChange("source", value);
    },
    [trackFieldChange]
  );

  const handleAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddress(e.target.value);
      trackFieldChange("address", e.target.value);
    },
    [trackFieldChange]
  );

  const handleTotalPriceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTotalPrice(e.target.value);
      trackFieldChange("totalPrice", e.target.value);
    },
    [trackFieldChange]
  );

  const handleCommentsChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setComments(e.target.value);
      trackFieldChange("comments", e.target.value);
    },
    [trackFieldChange]
  );

  const handleAssignedToChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAssignedTo(e.target.value);
      trackFieldChange("assignedTo", e.target.value);
    },
    [trackFieldChange]
  );

  const handleParsedItemsChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setParsedItems(e.target.value);
      trackFieldChange("parsedItems", e.target.value);
    },
    [trackFieldChange]
  );

  // Parse products list for ProductListEditor
  const parsedProductList = useMemo(() => {
    try {
      const parsed = JSON.parse(parsedItems || "[]");
      if (Array.isArray(parsed)) {
        return parsed.map((item: any) => ({
          name: item.name || "",
          quantity: item.quantity?.toString() || "",
          unit: item.unit || "ცალი",
          // Support both legacy "price" field and new "unitPrice" field from external orders
          price: (item.price ?? item.unitPrice)?.toString() || "",
        }));
      }
      return [];
    } catch (error) {
      return [];
    }
  }, [parsedItems]);

  // Handle products change for ProductListEditor with auto-calculate total (point #2)
  const handleProductsChange = useCallback(
    (products: any[]) => {
      const json = JSON.stringify(
        products.map((p) => ({
          name: p.name,
          quantity: p.quantity || "1",
          unit: p.unit || "ცალი",
          price: p.price ? parseFloat(p.price) : undefined,
        }))
      );
      setParsedItems(json);
      trackFieldChange("parsedItems", json);
      
      // Auto-calculate total from products
      const calculatedTotal = products.reduce((sum, p) => {
        const price = parseFloat(p.price) || 0;
        const qty = parseFloat(p.quantity) || 1;
        return sum + (price * qty);
      }, 0);
      
      if (calculatedTotal > 0) {
        const newTotal = calculatedTotal.toFixed(2);
        setTotalPrice(newTotal);
        trackFieldChange("totalPrice", newTotal);
      }
    },
    [trackFieldChange]
  );

  // Handle adding a new delivery log entry
  const handleAddLogEntry = useCallback(async () => {
    if (!newLogEntry.trim() || !order) return;

    // Use standardized log format (no emoji - RoleIcon displays at render time)
    const lang = language === 'ka' ? 'ka' : 'en';
    const noteEntry = createLogEntry('manager', newLogEntry.trim(), lang);

    try {
      const updatedNotes = await appendNoteAtomic(order.id, noteEntry, false);

      setInternalNotes(updatedNotes);
      setNewLogEntry("");
      // Flash highlight on newest entry (index 0 after reverse)
      setFlashLogIndex(0);
      setTimeout(() => setFlashLogIndex(null), 2000);
      // Auto-scroll to top (newest entry) after adding
      requestAnimationFrame(() => {
        logScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["today-orders"] });
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  }, [newLogEntry, order, language, t, toast, queryClient]);

  // Handle mark as paid
  const handleMarkAsPaid = useCallback(async () => {
    if (!order) return;

    setLoading(true);
    try {
      const lang = language === 'ka' ? 'ka' : 'en';
      const noteEntry = createActivityEntry('manager', 'payment_received', undefined, lang);
      const updatedNotes = await appendNoteAtomic(order.id, noteEntry, false);

      const { error } = await supabase
        .from("orders")
        .update({ order_status: "New" })
        .eq("id", order.id);

      if (error) throw error;

      setInternalNotes(updatedNotes);
      setStatus("New");

      toast({
        title: t("common.success"),
        description: t("payment.paymentReceived"),
      });

      queryClient.invalidateQueries({ queryKey: ["orders"] });
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [order, language, t, toast, queryClient]);

  const handleClose = () => {
    const params = new URLSearchParams(window.location.search);
    const from = params.get("from");
    if (from === "archive") {
      navigate("/archive");
    } else if (["today", "needs_attention", "delivery", "all", "activity"].includes(from || "")) {
      navigate(`/orders?scope=${from}`);
    } else {
      navigate("/orders");
    }
  };

  const handleSave = useCallback(async () => {
    if (!order) return;

    overlay.startProcessing();
    setLoading(true);
    setValidationErrors({});

    try {
      // Validate inputs with zod
      const validationResult = orderDetailSchema.safeParse({
        orderNumber,
        address,
        totalPrice,
        comments,
        assignedTo,
        parsedItems,
      });

      if (!validationResult.success) {
        const errors: Record<string, string> = {};
        validationResult.error.errors.forEach((err) => {
          if (err.path[0]) {
            errors[err.path[0] as string] = err.message;
          }
        });
        setValidationErrors(errors);

        toast({
          title: t("orders.messages.validationError"),
          description: Object.values(errors)[0],
          variant: "destructive",
        });
        setLoading(false);
        overlay.fail("validation");
        return;
      }

      // Check for duplicate order number if changed
      if (orderNumber && orderNumber !== order.order_number) {
        const { data: existingOrders, error: duplicateError } = await supabase
          .from("orders")
          .select("id")
          .eq("order_number", orderNumber)
          .neq("id", order.id)
          .limit(1);

        if (duplicateError) {
          toast({
            title: t("orders.messages.updateError"),
            description: t("orders.messages.duplicateCheckError"),
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        if (existingOrders && existingOrders.length > 0) {
          toast({
            title: t("orders.duplicate.title"),
            description: t("orders.duplicate.description").replace("{{orderNumber}}", orderNumber),
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
      }

      // Build activity log entries for meaningful changes and append atomically
      const lang = language === "ka" ? "ka" : "en";
      const statusChanged = status !== order.order_status;
      const addressChanged = address !== (order.address || "");
      
      const noteEntries: string[] = [];
      if (statusChanged) {
        noteEntries.push(createActivityEntry("manager", "status_changed", status, lang));
      }
      if (addressChanged && address) {
        noteEntries.push(createActivityEntry("manager", "address_updated", undefined, lang));
      }

      // Atomic note append if there are entries
      if (noteEntries.length > 0) {
        await appendNoteAtomic(order.id, noteEntries.join("\n"), false);
      }

      const { error } = await supabase
        .from("orders")
        .update({
          order_number: orderNumber || null,
          order_status: status,
          category: category || null,
          context_tag: contextTag || null,
          assigned_to: assignedTo || null,
          parsed_items: parsedItems || null,
          address: address || null,
          total_price: totalPrice ? parseFloat(totalPrice) : null,
          comments: comments || null,
          requested_delivery_date: requestedDeliveryDate || null,
          // Editable customer fields (point #3)
          customer_name: customerName,
          customer_phone: customerPhone || null,
          source: source || "Manual",
        })
        .eq("id", order.id);

      if (error) {
        toast({
          title: t("orders.messages.updateError"),
          description: error.message || t("orders.messages.databaseUpdateError"),
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Build concise save proof: show key change + count
      const savedFieldLabels: Record<string, string> = {
        status: `${t("orders.table.status")} → ${t(`orders.statuses.${status.toLowerCase()}`)}`,
        totalPrice: `${t("orders.detail.price")} → ${totalPrice}${CURRENCY.symbol}`,
        address: t("orders.detail.address"),
        customerName: t("orders.detail.customer"),
        category: t("orders.table.category"),
        parsedItems: t("orders.detail.products"),
        contextTag: t("orders.detail.contextTag"),
        assignedTo: t("orders.detail.assignedTo"),
        source: t("orders.detail.source"),
        orderNumber: t("orders.detail.orderNumber"),
        comments: t("orders.detail.comments"),
        customerPhone: t("orders.detail.phone"),
        requestedDeliveryDate: t("orders.detail.deliveryDate"),
      };
      const changedFields = Array.from(dirtyFields);
      const keyChange = changedFields.length > 0 ? savedFieldLabels[changedFields[0]] || changedFields[0] : "";
      const remaining = changedFields.length - 1;
      const saveDescription = changedFields.length === 0
        ? t("orders.messages.updateSuccess")
        : remaining > 0
          ? `${keyChange} · +${remaining} ${remaining === 1 ? t("orders.detail.change") : t("orders.detail.changes")}`
          : keyChange;

      toast({
        title: t("orders.messages.updateSuccess"),
        description: saveDescription,
      });

      // SUCCESS CLOSURE LAW: Clear dirty state FIRST, then mark complete
      // This ensures externalIsDirty (hasUnsavedChanges) is recalculated before overlay.complete()
      setDirtyFields(new Set());
      overlay.complete(); // Now close will bypass confirmation
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      const params = new URLSearchParams(window.location.search);
      const from = params.get("from");
      if (["today", "needs_attention", "delivery", "all", "activity"].includes(from || "")) {
        navigate(`/orders?scope=${from}`);
      } else {
        navigate("/orders");
      }
    } catch (error: any) {
      overlay.fail(error.message);
      toast({
        title: t("common.error"),
        description: error.message || t("orders.messages.updateError"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [
    order,
    orderNumber,
    status,
    category,
    contextTag,
    assignedTo,
    parsedItems,
    address,
    totalPrice,
    comments,
    requestedDeliveryDate,
    internalNotes,
    language,
    t,
    toast,
    queryClient,
    navigate,
    overlay,
  ]);

  const handleDelete = useCallback(async () => {
    if (!order) return;

    setLoading(true);
    try {
      const { error } = await supabase.from("orders").delete().eq("id", order.id);

      if (error) {
        toast({
          title: t("orders.messages.deleteError"),
          description: error.message || t("orders.messages.databaseDeleteError"),
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      toast({
        title: t("common.success"),
        description: t("orders.messages.deleteSuccess"),
      });

      queryClient.invalidateQueries({ queryKey: ["orders"] });
      const params = new URLSearchParams(window.location.search);
      const from = params.get("from");
      if (["today", "needs_attention", "delivery", "all", "activity"].includes(from || "")) {
        navigate(`/orders?scope=${from}`);
      } else {
        navigate("/orders");
      }
      setShowDeleteDialog(false);
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message || t("orders.messages.deleteError"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [order, t, toast, queryClient, navigate]);

  // Dirty dot indicator for FieldStack
  const dirtyDot = useCallback(
    (fieldName: string) =>
      dirtyFields.has(fieldName) ? (
        <span className="inline-flex h-2 w-2 rounded-full bg-primary animate-pulse" title="Modified" />
      ) : undefined,
    [dirtyFields]
  );

  const breadcrumbs = [{ label: t("nav.orders"), href: "/orders" }, { label: t("orders.detail.title") }];

  // Loading state
  if (orderLoading) {
    return (
      <OverlayPage
        title={t("orders.detail.title")}
        breadcrumbs={breadcrumbs}
        isDirty={false}
        onClose={handleClose}
        primaryAction={{
          label: t("orders.detail.saveChanges"),
          onClick: () => {},
          disabled: true,
        }}
      >
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </OverlayPage>
    );
  }

  // Order not found
  if (!order) {
    return (
      <OverlayPage
        title={t("orders.detail.title")}
        breadcrumbs={breadcrumbs}
        isDirty={false}
        onClose={handleClose}
        primaryAction={{
          label: t("common.back"),
          onClick: handleClose,
        }}
      >
        <div className="flex items-center justify-center py-24">
          <p className="text-muted-foreground">{t("orders.notFound")}</p>
        </div>
      </OverlayPage>
    );
  }

  return (
    <>
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title={t("orders.deleteOrder")}
        description={`${t("orders.detail.deleteWarning")}${order.order_number ? ` ${t("orders.table.orderNumber")} #${order.order_number}` : ""}`}
        confirmText={t("common.delete")}
        onConfirm={handleDelete}
        variant="destructive"
      />
      
      {/* Order Number Change Confirmation Dialog (point #3) */}
      <ConfirmDialog
        open={showOrderNumberConfirm}
        onOpenChange={setShowOrderNumberConfirm}
        title={t("orders.detail.confirmOrderNumberChange") || "Confirm Order Number Change"}
        description={t("orders.detail.confirmOrderNumberChangeDesc") || "Changing the order number will affect tracking and invoices. Are you sure?"}
        confirmText={t("common.confirm") || "Confirm"}
        cancelText={t("common.cancel")}
        onConfirm={confirmOrderNumberChange}
        variant="warning"
      />

      {/* Terminal status override confirmation (Fatigue Audit Fix #1) */}
      <ConfirmDialog
        open={showStatusOverrideConfirm}
        onOpenChange={(open) => { if (!open) cancelStatusOverride(); }}
        title={t("statusControl.overrideTitle")}
        description={t("statusControl.overrideMessage")}
        confirmText={t("statusControl.overrideConfirm")}
        cancelText={t("common.cancel")}
        onConfirm={confirmStatusOverride}
        variant="warning"
      />

      <OverlayPage
        title={t("orders.detail.title")}
        breadcrumbs={breadcrumbs}
        entityRef={order.order_number ? `#${order.order_number}` : `#${order.id.slice(0, 8)}`}
        isDirty={hasUnsavedChanges}
        isProcessing={overlay.state === "processing"}
        overlayState={overlay}
        onClose={handleClose}
        primaryAction={{
          label: loading ? t("orders.detail.saving") : overlay.state === "error" ? t("common.retrySave") : t("common.save"),
          onClick: handleSave,
          loading: loading,
          disabled: loading,
        }}
        secondaryAction={{
          label: t("common.cancel"),
          onClick: handleClose,
        }}
      >
        <OverlayContent>
          <OverlaySectionGroup>
            {/* Changes indicator - subtle inline */}
            {hasUnsavedChanges && (
              <p className="text-xs text-primary font-medium">
                {dirtyFields.size} {dirtyFields.size === 1 ? t("orders.detail.change") : t("orders.detail.changes")}
              </p>
            )}

            {/* Section 1: Order Info */}
            <OverlaySection title={t("orders.detail.sectionStatus")}>
              <div className="space-y-3">
                <FieldStack label={t("orders.detail.orderNumber")} htmlFor="orderNumber" indicator={dirtyDot("orderNumber")}>
                  <Input
                    id="orderNumber"
                    placeholder="#..."
                    value={orderNumber}
                    onChange={handleOrderNumberChange}
                    onBlur={handleOrderNumberBlur}
                    className="h-10"
                    aria-invalid={!!validationErrors.orderNumber}
                  />
                </FieldStack>

                <FieldStack label={t("orders.table.status")} htmlFor="status" indicator={dirtyDot("status")}>
                  <Select value={status} onValueChange={handleStatusChange}>
                    <SelectTrigger id="status" className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background z-50">
                      {/* Current status always shown */}
                      <SelectItem key={status} value={status}>
                        {t(`orders.statuses.${status.toLowerCase()}`)}
                      </SelectItem>
                      {/* Valid next statuses from state machine */}
                      {getNextStatuses(status).map((stat) => (
                        <SelectItem key={stat} value={stat}>
                          {t(`orders.statuses.${stat.toLowerCase()}`)}
                        </SelectItem>
                      ))}
                      {/* Override options — always visible */}
                      {ALL_OVERRIDE_STATUSES.filter(s => s !== status && !getNextStatuses(status).includes(s)).length > 0 && (
                        <SelectGroup>
                          <SelectSeparator />
                          <SelectLabel className="text-xs text-muted-foreground py-1 pl-8">{t("statusControl.overrideStatus")}</SelectLabel>
                      {ALL_OVERRIDE_STATUSES.filter(s => s !== status && !getNextStatuses(status).includes(s)).map((stat) => (
                            <SelectItem key={stat} value={stat} className="opacity-60">
                              {t(`orders.statuses.${stat.toLowerCase()}`)}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </FieldStack>

                <FieldStack label={t("orders.table.category")} htmlFor="category" indicator={dirtyDot("category")}>
                  <Select value={category} onValueChange={handleCategoryChange}>
                    <SelectTrigger id="category" className="h-10">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent className="bg-background z-50">
                      {ORDER_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {getCategoryLabel(cat, language)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldStack>

                <FieldStack label={t("orders.detail.source")} htmlFor="source" indicator={dirtyDot("source")}>
                  <Select value={source} onValueChange={handleSourceChange}>
                    <SelectTrigger id="source" className="h-10">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent className="bg-background z-50">
                      {ORDER_SOURCES.map((src) => (
                        <SelectItem key={src} value={src}>
                          {getSourceLabel(src, language)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldStack>

                {/* Payment Method - integrated row */}
                {(() => {
                  const paymentMethod = order.payment_method;
                  const isAwaitingPayment = status === "Awaiting Payment";
                  
                  if (!paymentMethod && !isAwaitingPayment) return null;
                  
                  let label = "";
                  if (paymentMethod === 'cash') {
                    label = t("payment.cashOnDelivery");
                  } else if (paymentMethod === 'transfer' && isAwaitingPayment) {
                    label = t("payment.transferAwaitingConfirm");
                  } else if (paymentMethod === 'transfer') {
                    label = t("payment.bankTransfer");
                  } else if (isAwaitingPayment) {
                    label = t("payment.awaitingPayment");
                  }
                  
                  return (
                    <div>
                      <Label className="text-sm text-muted-foreground">
                        {t("payment.paymentMethod")}
                      </Label>
                      <div className={cn(
                        "mt-1 flex items-center justify-between gap-2 min-h-[40px] px-3 py-2 rounded-md border",
                        isAwaitingPayment
                          ? "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800"
                          : "bg-muted/30 border-border"
                      )}>
                        <span className={cn(
                          "text-sm min-w-0 truncate",
                          isAwaitingPayment
                            ? "text-amber-700 dark:text-amber-300"
                            : "text-muted-foreground"
                        )}>
                          {label}
                        </span>
                        {isAwaitingPayment && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowPaymentConfirm(true)}
                            disabled={loading}
                            className="h-7 text-xs shrink-0 text-amber-700 dark:text-amber-300 hover:text-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/50"
                          >
                            {t("payment.confirmReceived")}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </OverlaySection>

            {/* Payment Confirmation Dialog (Fix A: Trust Audit) */}
            <ConfirmDialog
              open={showPaymentConfirm}
              onOpenChange={setShowPaymentConfirm}
              title={t("payment.confirmPayment")}
              description={t("payment.confirmPaymentDesc")}
              confirmText={t("payment.confirmReceived")}
              onConfirm={async () => {
                await handleMarkAsPaid();
                setShowPaymentConfirm(false);
              }}
              loading={loading}
              variant="default"
            />

            {/* Section 2: Customer & Delivery */}
            <OverlaySection title={t("orders.detail.sectionCustomer")}>
              <div className="space-y-3">
                <FieldStack label={t("orders.table.customer")} htmlFor="customerName" indicator={dirtyDot("customerName")}>
                  <Input
                    id="customerName"
                    placeholder={t("orders.placeholders.customerName")}
                    value={customerName}
                    onChange={handleCustomerNameChange}
                    className="h-10"
                  />
                </FieldStack>

                <FieldStack label={t("orders.detail.phone")} htmlFor="customerPhone" indicator={dirtyDot("customerPhone")}>
                  <Input
                    id="customerPhone"
                    placeholder={t("orders.placeholders.customerPhone")}
                    value={customerPhone}
                    onChange={handleCustomerPhoneChange}
                    className="h-10"
                  />
                </FieldStack>

                {/* Tracking Link - only show when shortcode exists */}
                {(order as any)?.tracking_shortcode && (
                  <TrackingLinkRow shortcode={(order as any).tracking_shortcode} t={t} />
                )}

                <FieldStack label={t("orders.table.address")} htmlFor="address" indicator={dirtyDot("address")} error={validationErrors.address}>
                  <Input
                    id="address"
                    placeholder={t("orders.placeholders.noAddress")}
                    value={address}
                    onChange={handleAddressChange}
                    className="h-10"
                    aria-invalid={!!validationErrors.address}
                  />
                </FieldStack>

                <FieldStack label={t("orders.labels.requestedDeliveryDate") || "Delivery Date"} htmlFor="requestedDeliveryDate" indicator={dirtyDot("requestedDeliveryDate")}>
                  <DeliveryDatePicker
                    value={requestedDeliveryDate}
                    onChange={(date) => {
                      setRequestedDeliveryDate(date);
                      trackFieldChange("requestedDeliveryDate", date || "");
                    }}
                    allowPast
                  />
                </FieldStack>
              </div>
            </OverlaySection>

            {/* Section 3: Products — No header (point #1) */}
            <OverlaySection>
              {/* Raw JSON toggle - only in development mode */}
              {process.env.NODE_ENV === 'development' && (
                <div className="flex items-center justify-end mb-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowRawParsed((v) => !v)}
                    className="h-7 px-2 text-xs text-muted-foreground"
                  >
                    {showRawParsed ? t("orders.detail.useEditor") : t("orders.detail.rawJSON")}
                  </Button>
                </div>
              )}

              {showRawParsed ? (
                <>
                  <Textarea
                    id="parsedItems"
                    placeholder={t("orders.placeholders.parsedItems")}
                    value={parsedItems}
                    onChange={handleParsedItemsChange}
                    className="min-h-[120px] font-mono text-xs"
                    aria-invalid={!!validationErrors.parsedItems}
                  />
                  {validationErrors.parsedItems && (
                    <p className="text-sm text-destructive mt-1">{validationErrors.parsedItems}</p>
                  )}
                </>
              ) : (
                <>
                  <ProductListEditor products={parsedProductList} onProductsChange={handleProductsChange} />
                  {validationErrors.parsedItems && (
                    <p className="text-sm text-destructive mt-1">{validationErrors.parsedItems}</p>
                  )}
                </>
              )}

              {/* Total Price - using InlineSummaryRow */}
              <InlineSummaryRow
                label={`${t("orders.detail.price")} (${CURRENCY.symbol})`}
                value={
                  <Input
                    id="totalPrice"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={totalPrice}
                    onChange={handleTotalPriceChange}
                    className="w-28 h-9 text-right"
                    aria-invalid={!!validationErrors.totalPrice}
                  />
                }
                showBorder
              />
            </OverlaySection>

            {/* Section 4: Shared Delivery Log (unified — replaces old timeline + log) */}
            <OverlaySection title={t("deliveryLog.title")} showBorder>
              {internalNotes ? (
                <div ref={logScrollRef} className="max-h-48 overflow-y-auto rounded-lg bg-muted/30 p-3 mb-3">
                  <div className="text-sm leading-relaxed space-y-0">
                    {internalNotes.split("\n").filter(Boolean).reverse().map((line, idx, arr) => {
                      const role = parseLogRole(line);
                      const isLast = idx === arr.length - 1;
                      const displayText = stripRoleTokenForDisplay(line);
                      
                      // Separate message from meta suffix
                      const metaSeparator = '| meta:';
                      const hasMeta = displayText.includes(metaSeparator);
                      const message = hasMeta 
                        ? displayText.split(metaSeparator)[0].trim() 
                        : displayText;
                      const meta = hasMeta 
                        ? displayText.split(metaSeparator)[1]?.trim() 
                        : null;
                      
                      return (
                        <div
                          key={idx}
                          className={cn(
                            "py-1.5 flex items-start gap-2 transition-colors duration-1000",
                            !isLast && "border-b border-border/30",
                            flashLogIndex === idx && "bg-primary/10 rounded-md -mx-1 px-1"
                          )}
                        >
                          {role && <RoleIcon role={role} size="md" className="mt-0.5 shrink-0" />}
                          <div className="min-w-0 flex-1">
                            {/* Message: 2-line clamp */}
                            <span className={cn(
                              "text-sm line-clamp-2 break-words",
                              role === "courier" ? "text-primary" 
                                : role === "manager" ? "text-foreground" 
                                : "text-muted-foreground"
                            )}>
                              {message}
                            </span>
                            {/* Meta: collapsed, monospace, muted */}
                            {meta && (
                              <span className="block text-xs font-mono text-muted-foreground/60 mt-0.5 truncate">
                                {meta}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic mb-3">{t("deliveryLog.emptyState")}</p>
              )}

              <div className="flex gap-2">
                <Input
                  placeholder={t("deliveryLog.placeholder")}
                  value={newLogEntry}
                  onChange={(e) => setNewLogEntry(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleAddLogEntry();
                    }
                  }}
                  className="flex-1 h-9"
                />
                <Button size="icon" className="h-9 w-9" onClick={handleAddLogEntry} disabled={!newLogEntry.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </OverlaySection>

            {/* Section 6: Meta & Additional Details — Collapsed by default */}
            <OverlaySection 
              title={t("orders.labels.moreDetails")}
              collapsible 
              defaultOpen={false}
            >
              {/* Timestamps moved here (subtle, read-only) */}
              <div className="space-y-1 pb-3 mb-3 border-b border-border/50">
                <OverlaySummaryRow label={t("orders.detail.createdAt")} muted>
                  {formattedCreatedDate} {formattedCreatedTime}
                </OverlaySummaryRow>
                <OverlaySummaryRow label={t("orders.labels.lastUpdated")} muted>
                  {formattedUpdatedDate} {formattedUpdatedTime}
                </OverlaySummaryRow>
                {order.csr_name && (
                  <OverlaySummaryRow label={t("orders.labels.postedBy")} muted>
                    {order.csr_name}
                  </OverlaySummaryRow>
                )}
              </div>

              {/* Context Tag - Only show if customer has orderContexts configured */}
              {orderContexts.length > 0 && (
                <FieldStack label={t("orders.labels.context")} htmlFor="contextTag" indicator={dirtyDot("contextTag")} helper={t("orders.labels.contextHint")}>
                  {status === "New" || status === "Completed" ? (
                    <ContextTagSelect value={contextTag} onChange={handleContextTagChange} options={orderContexts} />
                  ) : contextTag ? (
                    <ContextTagBadge tag={contextTag} />
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </FieldStack>
              )}

              <FieldStack label={t("orders.detail.assignedTo")} htmlFor="assignedTo" indicator={dirtyDot("assignedTo")} error={validationErrors.assignedTo}>
                <Input
                  id="assignedTo"
                  placeholder={t("orders.placeholders.managerName")}
                  value={assignedTo}
                  onChange={handleAssignedToChange}
                  className="h-9"
                  aria-invalid={!!validationErrors.assignedTo}
                />
              </FieldStack>

              <FieldStack label={t("orders.detail.comments")} htmlFor="comments" indicator={dirtyDot("comments")} error={validationErrors.comments}>
                <Textarea
                  id="comments"
                  placeholder={t("orders.placeholders.comments")}
                  value={comments}
                  onChange={handleCommentsChange}
                  className="min-h-[60px]"
                  aria-invalid={!!validationErrors.comments}
                />
              </FieldStack>

              <FieldStack label={t("orders.detail.rawInput")} htmlFor="rawInput">
                <Textarea
                  value={order.message_raw}
                  disabled
                  className="min-h-[80px] bg-muted/50 text-xs"
                />
              </FieldStack>

              {/* Order ID */}
              <OverlaySummaryRow label={t("orders.labels.orderId")} muted>
                <code className="text-xs font-mono">{order.id}</code>
              </OverlaySummaryRow>
              
              {/* Delete Order - Moved here per point #6 */}
              <div className="pt-4 mt-4 border-t border-border/50">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("orders.deleteOrder")}
                </Button>
              </div>
            </OverlaySection>
          </OverlaySectionGroup>
        </OverlayContent>
      </OverlayPage>
    </>
  );
}

/** Tracking link row with copy button */
function TrackingLinkRow({ shortcode, t }: { shortcode: string; t: (key: string) => string }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/t/${shortcode}`;
  
  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    sonnerToast.success(t("orders.detail.trackingLinkCopied"));
    setTimeout(() => setCopied(false), 2000);
  }, [url, t]);
  
  return (
    <FieldStack label={t("orders.detail.trackingLink")}>
      <div className="flex items-center gap-2">
        <code className="text-xs font-mono text-muted-foreground truncate flex-1 bg-muted/50 rounded px-2 py-1.5">
          /t/{shortcode}
        </code>
        <Button variant="outline" size="sm" className="shrink-0 h-8" onClick={handleCopy}>
          {copied ? (
            <Check className="h-3.5 w-3.5 mr-1 text-emerald-500" />
          ) : (
            <Copy className="h-3.5 w-3.5 mr-1" />
          )}
          {t("orders.detail.copyTrackingLink")}
        </Button>
      </div>
    </FieldStack>
  );
}
